import React, { useState, useEffect } from 'react';
import { Camera, Receipt, FileSpreadsheet, Eye, Send, Package, FileText, Settings } from 'lucide-react';
import { supabase } from './supabaseClient';
import { generateFotosPDF, generateRecibosPDF } from './utils/generateManifestPDF';
import { generateCosteoXLSX } from './utils/generateCosteoXLSX';
import { generateAnexoPDF, scanAnexoHighValue } from './utils/generateAnexoPDF';
import { buildCosteoData } from './LiveManifest';
import DeclarationReview from './DeclarationReview';
import CustomsSettings from './CustomsSettings';
import StoreSettings from './StoreSettings';
import AnexoSplitModal from './AnexoSplitModal';

const ProductionTab = ({ user, userPermissions }) => {
  const [activeTab, setActiveTab] = useState('exports');
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [exportingFotosId,   setExportingFotosId]   = useState(null);
  const [exportingRecibosId, setExportingRecibosId] = useState(null);
  const [exportingCosteoId,  setExportingCosteoId]  = useState(null);
  const [exportingAnexoId,   setExportingAnexoId]   = useState(null);

  // Split-modal state: { manifestId, scanResult } | null
  const [splitModalState, setSplitModalState] = useState(null);
  // Staged individual PDFs: { [manifestId]: [{ seq, label, total, bytes, downloaded }] }
  const [stagedIndividuals, setStagedIndividuals] = useState({});

  useEffect(() => {
    const fetchClosed = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('manifests')
          .select('*')
          .eq('status', 'CLOSED')
          .order('closed_at', { ascending: false });
        if (error) throw error;
        setManifests(data || []);
      } catch (err) {
        console.error('[Producción] Failed to fetch manifests:', err);
        setManifests([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClosed();
  }, []);

  const handleToggle = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const handleExportFotos = async (m) => {
    setExportingFotosId(m.id);
    try {
      await generateFotosPDF(m.id, supabase);
    } catch (err) {
      console.error('[Producción] Fotos PDF error:', err);
      alert('Error generando PDF de fotos. Intente de nuevo.');
    } finally {
      setExportingFotosId(null);
    }
  };

  const handleExportRecibos = async (m) => {
    setExportingRecibosId(m.id);
    try {
      await generateRecibosPDF(m.id, supabase);
    } catch (err) {
      console.error('[Producción] Recibos PDF error:', err);
      alert('Error generando PDF de recibos. Intente de nuevo.');
    } finally {
      setExportingRecibosId(null);
    }
  };

  // Rewritten: scan first, open modal if high-value packages exist
  const handleExportAnexo = async (m) => {
    setExportingAnexoId(m.id);
    try {
      const scanResult = await scanAnexoHighValue(m.id, supabase);
      if (scanResult.highValue.length === 0) {
        // No packages above threshold — normal single-PDF flow unchanged
        await generateAnexoPDF(m.id, supabase);
      } else {
        // Open split modal; PDF generation deferred until user confirms
        setSplitModalState({ manifestId: m.id, scanResult });
      }
    } catch (err) {
      console.error('[Anexo split] scan error:', err);
      alert('Error al escanear paquetes: ' + (err.message || ''));
    } finally {
      setExportingAnexoId(null);
    }
  };

  // Called by modal with the list of seqs to extract as individuals
  const handleSplitConfirm = async (individualSeqs) => {
    const { manifestId, scanResult } = splitModalState;
    setSplitModalState(null); // close modal immediately

    setExportingAnexoId(manifestId);
    try {
      // (a) Main PDF fires on the confirm-click gesture — skipSeqs removes the extracted packages
      await generateAnexoPDF(manifestId, supabase, { skipSeqs: individualSeqs });

      // (b) Stage each individual PDF as bytes (no download yet)
      const staged = [];
      for (const seq of individualSeqs) {
        try {
          const bytes = await generateAnexoPDF(manifestId, supabase, { onlySeq: seq, returnBytes: true });
          const pkgInfo = scanResult.all.find(p => p.seq === seq);
          staged.push({
            seq,
            label:      pkgInfo?.label  || `Paquete ${seq}`,
            total:      pkgInfo?.total  || 0,
            bytes,
            downloaded: false,
          });
        } catch (err) {
          console.error('[Anexo split] Error staging seq', seq, err);
          alert(`Error generando Anexo Individual paquete ${seq}: ` + (err.message || ''));
        }
      }

      setStagedIndividuals(prev => ({ ...prev, [manifestId]: staged }));
      console.log('[Anexo split] staged', staged.length, 'individual PDFs for manifest', manifestId);
    } catch (err) {
      console.error('[Anexo split] handleSplitConfirm error:', err);
      alert('Error generando Anexo: ' + (err.message || ''));
    } finally {
      setExportingAnexoId(null);
    }
  };

  // Download a staged individual PDF and mark it as downloaded
  const handleDownloadStaged = (manifestId, seq) => {
    const manifest = stagedIndividuals[manifestId] || [];
    const item = manifest.find(i => i.seq === seq);
    if (!item) return;

    try {
      const blob = new Blob([item.bytes], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Anexo_Individual_${seq}_${manifestId}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      setStagedIndividuals(prev => ({
        ...prev,
        [manifestId]: (prev[manifestId] || []).map(i =>
          i.seq === seq ? { ...i, downloaded: true } : i
        ),
      }));
    } catch (err) {
      console.error('[Anexo split] download staged error:', err);
      alert('Error al descargar el Anexo Individual. Intente de nuevo.');
    }
  };

  const handleExportCosteo = async (m) => {
    setExportingCosteoId(m.id);
    try {
      const data = await buildCosteoData(m);
      if (!data) {
        alert('No hay paquetes ni encomiendas en este manifiesto.');
        return;
      }
      generateCosteoXLSX(data);
    } catch (err) {
      console.error('[Producción] Costeo error:', err);
      alert('Error generando costeo: ' + (err.message || ''));
    } finally {
      setExportingCosteoId(null);
    }
  };

  if (activeTab === 'declaration') {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="tile" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setActiveTab('exports')}
              className="btn btn-secondary"
              style={{ fontSize: '13px', padding: '6px 14px' }}
            >
              ← Exportaciones
            </button>
          </div>
        </div>
        <DeclarationReview user={user} userPermissions={userPermissions} />
      </div>
    );
  }

  if (activeTab === 'customsSettings') {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="tile" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setActiveTab('exports')}
              className="btn btn-secondary"
              style={{ fontSize: '13px', padding: '6px 14px' }}
            >
              ← Exportaciones
            </button>
          </div>
        </div>
        <CustomsSettings user={user} userPermissions={userPermissions} />
      </div>
    );
  }

  if (activeTab === 'storeSettings') {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="tile" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setActiveTab('exports')}
              className="btn btn-secondary"
              style={{ fontSize: '13px', padding: '6px 14px' }}
            >
              ← Exportaciones
            </button>
          </div>
        </div>
        <StoreSettings user={user} userPermissions={userPermissions} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="tile max-w-5xl mx-auto" style={{ padding: '40px', textAlign: 'center' }}>
        <span className="metric-label">Cargando manifiestos cerrados…</span>
      </div>
    );
  }

  if ((manifests || []).length === 0) {
    return (
      <div className="tile max-w-5xl mx-auto" style={{ padding: '40px', textAlign: 'center' }}>
        <Package size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
        <span className="metric-label">No hay manifiestos cerrados todavía.</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Split modal — rendered at top level, outside the map */}
      {splitModalState && (
        <AnexoSplitModal
          isOpen={true}
          onClose={() => setSplitModalState(null)}
          scanResult={splitModalState.scanResult}
          onConfirm={handleSplitConfirm}
        />
      )}

      <div className="tile" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Producción</h2>
            <p className="metric-sub" style={{ margin: '4px 0 0' }}>
              Selecciona un manifiesto cerrado para iniciar el proceso de exportación a Genera.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {userPermissions?.canViewDeclaration && (
              <button
                type="button"
                onClick={() => setActiveTab('declaration')}
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FileText size={14} /> Declaración DUA
              </button>
            )}
            {userPermissions?.canEditCustomsConfig && (
              <button
                type="button"
                onClick={() => setActiveTab('customsSettings')}
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Settings size={14} /> Config Aduanas
              </button>
            )}
            {userPermissions?.canEditCustomsConfig && (
              <button
                type="button"
                onClick={() => setActiveTab('storeSettings')}
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Settings size={14} /> Datos de Tienda
              </button>
            )}
            <div className="badge" style={{ background: 'var(--blue-soft)', color: 'var(--blue-text)' }}>
              {manifests.length} cerrados
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {(manifests || []).map(m => {
          const isExpanded = expandedId === m.id;
          const closedDate = m.closed_at
            ? new Date(m.closed_at).toLocaleDateString('es-SV', { year: 'numeric', month: 'short', day: 'numeric' })
            : '—';
          const staged = stagedIndividuals[m.id] || [];

          return (
            <div key={m.id} className="tile" style={{ padding: 0, overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => handleToggle(m.id)}
                style={{
                  width: '100%', padding: '16px 20px', background: 'transparent', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Package size={20} style={{ color: 'var(--blue)' }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '15px' }}>{m.id || '—'}</div>
                    <div className="metric-sub">Cerrado {closedDate}{m.closed_by ? ` · ${m.closed_by}` : ''}</div>
                  </div>
                </div>
                <span style={{ fontSize: '18px', color: 'var(--text-tertiary)' }}>{isExpanded ? '▾' : '▸'}</span>
              </button>

              {isExpanded && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px', background: 'var(--bg)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={() => handleExportFotos(m)}
                      disabled={exportingFotosId === m.id}
                      className="btn btn-secondary"
                      style={{ justifyContent: 'center', borderColor: 'var(--blue)', color: 'var(--blue)' }}
                    >
                      <Camera size={16} style={{ marginRight: '6px' }} />
                      {exportingFotosId === m.id ? '…' : 'Fotos PDF'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExportRecibos(m)}
                      disabled={exportingRecibosId === m.id}
                      className="btn btn-secondary"
                      style={{ justifyContent: 'center', borderColor: 'var(--amber, #d97706)', color: 'var(--amber, #d97706)' }}
                    >
                      <Receipt size={16} style={{ marginRight: '6px' }} />
                      {exportingRecibosId === m.id ? '…' : 'Recibos PDF'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExportCosteo(m)}
                      disabled={exportingCosteoId === m.id}
                      className="btn btn-secondary"
                      style={{ justifyContent: 'center' }}
                    >
                      <FileSpreadsheet size={16} style={{ marginRight: '6px' }} />
                      {exportingCosteoId === m.id ? '…' : 'Costeo XLSX'}
                    </button>
                    {userPermissions?.canExportCosteo && (
                      <button
                        type="button"
                        onClick={() => handleExportAnexo(m)}
                        disabled={exportingAnexoId === m.id}
                        className="btn btn-secondary"
                        style={{ justifyContent: 'center', borderColor: 'var(--green, #16a34a)', color: 'var(--green, #16a34a)' }}
                      >
                        <FileText size={16} style={{ marginRight: '6px' }} />
                        {exportingAnexoId === m.id ? '…' : '📋 Generar Anexo'}
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
                    <button type="button" disabled className="btn btn-secondary" style={{ justifyContent: 'center', opacity: 0.5, cursor: 'not-allowed' }}>
                      <Eye size={16} style={{ marginRight: '6px' }} />
                      Vista previa Genera (Próximamente)
                    </button>
                    <button type="button" disabled className="btn btn-secondary" style={{ justifyContent: 'center', opacity: 0.5, cursor: 'not-allowed' }}>
                      <Send size={16} style={{ marginRight: '6px' }} />
                      Exportar para Genera (Próximamente)
                    </button>
                  </div>

                  {/* Staged individual PDFs panel */}
                  {staged.length > 0 && (
                    <div style={{
                      marginTop: '16px', padding: '12px 16px',
                      border: '1px solid var(--border)', borderRadius: '8px',
                      background: 'var(--bg)',
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: '10px',
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text)' }}>
                          Anexos individuales pendientes ({staged.length})
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setStagedIndividuals(prev => {
                              const next = { ...prev };
                              delete next[m.id];
                              return next;
                            })
                          }
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '12px', color: 'var(--text-tertiary)',
                            textDecoration: 'underline', padding: 0,
                          }}
                        >
                          Limpiar
                        </button>
                      </div>

                      {staged.map(item => (
                        <div
                          key={item.seq}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: '12px', padding: '8px 0',
                            borderTop: '1px solid var(--border)',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: '13px', fontWeight: 600, color: 'var(--text)',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              #{item.seq} — {item.label}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                              ${item.total.toFixed(2)} USD
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDownloadStaged(m.id, item.seq)}
                            style={{
                              padding: '6px 12px', flexShrink: 0,
                              background: item.downloaded ? 'transparent' : 'var(--blue)',
                              color: item.downloaded ? 'var(--text-tertiary)' : '#fff',
                              border: item.downloaded ? '1px solid var(--border)' : 'none',
                              borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            {item.downloaded ? '✓ Descargado — Descargar de nuevo' : 'Descargar'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductionTab;
